import { registrarError, traducirErrorSupabase } from '../../domain/errores';
import { aBooleano, aNumero, aObjeto, aTexto } from '../mappers/conversiones';
import { supabase } from '../supabase/cliente';

const TABLA_HORARIOS = 'actuadores_horarios';
const TABLA_REGLAS = 'umbrales_actuadores_auto';

/**
 * Regla horaria: acciona un equipo por reloj, sin depender de ningún sensor.
 * Sirve para cosas como encender el riego a las seis de la mañana.
 */
export interface ReglaHoraria {
  /** UUID. */
  id: string;
  actuadorId: number;
  actuadorNombre: string;
  ubicacion: string;
  /** `HH:mm` */
  horaInicio: string;
  horaFin: string;
  /** Días en los que aplica; 0 = domingo. */
  dias: number[];
  tipoOrden: 'set' | 'pulse';
  carga: Record<string, unknown>;
  /** Clasificación propia de la tabla; se conserva al guardar. */
  tipo: string | null;
  activo: boolean;
}

export interface DatosReglaHoraria {
  actuador_id: number;
  hora_inicio: string;
  hora_fin: string;
  dias: number[];
  cmd_kind: string;
  cmd_payload: Record<string, unknown>;
  activo: boolean;
}

/**
 * Regla por evento: acciona un equipo cuando se dispara un umbral.
 * Se define desde la pantalla de umbrales; aquí sólo se consulta, para poder
 * ver en un mismo sitio todo lo que puede accionar un equipo solo.
 */
export interface ReglaPorEvento {
  id: string;
  actuadorId: number;
  actuadorNombre: string;
  umbralId: string;
  tipoUmbral: string;
  tipoOrden: string;
  carga: Record<string, unknown>;
  restablecerAutomatico: boolean;
  activo: boolean;
}

/**
 * Automatismos de los actuadores.
 *
 * Hay dos formas de que un equipo se accione solo: por reloj (regla horaria) o
 * porque saltó un umbral (regla por evento). Se leen juntas porque para el
 * usuario la pregunta es una sola: «¿qué puede mover este equipo sin que yo
 * lo toque?».
 */
export class RepositorioAutomatizacion {
  async listarHorarias(): Promise<ReglaHoraria[]> {
    const { data, error } = await supabase.from(TABLA_HORARIOS).select('*');
    if (error) throw traducirErrorSupabase(error);

    const filas = (data ?? []) as Record<string, unknown>[];
    const actuadores = await this.leerActuadores(
      filas.map((f) => aNumero(f.actuador_id)).filter((id): id is number => id !== null),
    );

    return filas
      .map((fila) => {
        const actuadorId = aNumero(fila.actuador_id) ?? 0;
        const datos = actuadores.get(actuadorId);

        return {
          id: aTexto(fila.id) ?? '',
          actuadorId,
          actuadorNombre: datos?.nombre ?? `Actuador ${actuadorId}`,
          ubicacion: datos?.ubicacion ?? '—',
          horaInicio: (aTexto(fila.hora_inicio) ?? '').slice(0, 5),
          horaFin: (aTexto(fila.hora_fin) ?? '').slice(0, 5),
          dias: this.aDias(fila.dias),
          tipoOrden: aTexto(fila.cmd_kind) === 'pulse' ? ('pulse' as const) : ('set' as const),
          carga: aObjeto(fila.cmd_payload),
          tipo: aTexto(fila.tipo),
          activo: aBooleano(fila.activo) ?? true,
        };
      })
      .sort((a, b) => a.actuadorNombre.localeCompare(b.actuadorNombre, 'es'));
  }

  async listarPorEvento(): Promise<ReglaPorEvento[]> {
    const { data, error } = await supabase.from(TABLA_REGLAS).select('*');
    if (error) throw traducirErrorSupabase(error);

    const filas = (data ?? []) as Record<string, unknown>[];
    const actuadores = await this.leerActuadores(
      filas.map((f) => aNumero(f.actuador_id)).filter((id): id is number => id !== null),
    );

    return filas
      .map((fila) => {
        const actuadorId = aNumero(fila.actuador_id) ?? 0;

        return {
          id: aTexto(fila.id) ?? '',
          actuadorId,
          actuadorNombre: actuadores.get(actuadorId)?.nombre ?? `Actuador ${actuadorId}`,
          umbralId: aTexto(fila.umbral_id) ?? '',
          tipoUmbral: aTexto(fila.tipo_umbral) ?? '',
          tipoOrden: aTexto(fila.cmd_kind) ?? 'set',
          carga: aObjeto(fila.cmd_payload),
          restablecerAutomatico: aBooleano(fila.restablecer_automatico) ?? false,
          activo: aBooleano(fila.activo) ?? true,
        };
      })
      .sort((a, b) => a.actuadorNombre.localeCompare(b.actuadorNombre, 'es'));
  }

  async crearHoraria(datos: DatosReglaHoraria): Promise<void> {
    const { error } = await supabase.from(TABLA_HORARIOS).insert(datos);
    if (error) throw traducirErrorSupabase(error);
  }

  async actualizarHoraria(id: string, datos: Partial<DatosReglaHoraria>): Promise<void> {
    const { error } = await supabase.from(TABLA_HORARIOS).update(datos).eq('id', id);
    if (error) throw traducirErrorSupabase(error);
  }

  async eliminarHoraria(id: string): Promise<void> {
    const { error } = await supabase.from(TABLA_HORARIOS).delete().eq('id', id);
    if (error) throw traducirErrorSupabase(error);
  }

  async cambiarActivoHoraria(id: string, activo: boolean): Promise<void> {
    return this.actualizarHoraria(id, { activo });
  }

  /** Desactiva una regla por evento sin tener que ir a la pantalla de umbrales. */
  async cambiarActivoPorEvento(id: string, activo: boolean): Promise<void> {
    const { error } = await supabase.from(TABLA_REGLAS).update({ activo }).eq('id', id);
    if (error) throw traducirErrorSupabase(error);
  }

  private aDias(valor: unknown): number[] {
    if (!Array.isArray(valor)) return [0, 1, 2, 3, 4, 5, 6];

    const dias = valor
      .map((d) => aNumero(d))
      .filter((d): d is number => d !== null && d >= 0 && d <= 6);

    return dias.length > 0 ? dias : [0, 1, 2, 3, 4, 5, 6];
  }

  private async leerActuadores(
    ids: readonly number[],
  ): Promise<Map<number, { nombre: string; ubicacion: string }>> {
    const mapa = new Map<number, { nombre: string; ubicacion: string }>();
    if (ids.length === 0) return mapa;

    const { data, error } = await supabase
      .from('v3_vw_actuadores_control_ui')
      .select('id, nombre, nodo_nombre, area_nombre, cliente_nombre')
      .in('id', [...new Set(ids)]);

    if (error) {
      registrarError('automatizacion.actuadores', error);
      return mapa;
    }

    for (const fila of (data ?? []) as Record<string, unknown>[]) {
      const id = aNumero(fila.id);
      if (id === null) continue;

      mapa.set(id, {
        nombre: aTexto(fila.nombre) ?? `Actuador ${id}`,
        ubicacion:
          [aTexto(fila.cliente_nombre), aTexto(fila.area_nombre), aTexto(fila.nodo_nombre)]
            .filter(Boolean)
            .join(' · ') || '—',
      });
    }

    return mapa;
  }
}

export const repositorioAutomatizacion = new RepositorioAutomatizacion();
